import { SettlementChances } from "../../../shared/settlementProspects";
import Parse, { ACL, Attributes } from "parse";
import { ReactNode, useContext } from "react";
import parseAuth from "../../../components/auth/parseAuth";
import { roleName } from "../../../shared/AccessList.ts";
import { PartyAgreements } from "../../../shared/Agreements.ts";
import { CaseCounts } from "../../../shared/CaseCounts.ts";
import {
  CASE_PARAMETERS_KEYS,
  CaseExport,
  importedBookings,
  toCaseParameters,
} from "../../../shared/caseExport.ts";
import {
  BookedReserve,
  ClaimKind,
  Case,
  CaseParameters,
  Claim,
  Damage,
  Configuration,
  CostStructure,
  Party,
  Results,
  SettlementOffer,
  Variants,
} from "../../../shared/CaseModel";
import { Timeline } from "../../../shared/timeline";
import { CaseListElement } from "../../../shared/CasesList.ts";
import { Budget } from "../../../shared/CostModel";
import { CaseFilter, QueryOptions } from "../../../shared/parse/CasesQuery.ts";
import {
  PortfolioOverview,
  PortfolioStats,
  SettlementPortfolio,
} from "../../../shared/PortfolioTypes.ts";
import { withCostItems } from "../../../shared/costItems.ts";
import { withDamages } from "../../../shared/damages.ts";
import randomId from "../../../shared/randomId.ts";
import { withSettlementCostTypes } from "../../../shared/settlementCosts.ts";
import { BudgetInitializingCaseApiDecorator } from "../BudgetInitializingCaseApiDecorator";
import { CaseApi, CaseApiContext } from "../CaseApi";
import { DecisionTreeResult, fetchDecisionTrees } from "../decisionTrees.ts";

// the SDK exports its Object and Query classes merged with option namespaces,
// which hides their type parameters; take the instance types from the values
type ParseObject<T extends Attributes = Attributes> = InstanceType<
  typeof Parse.Object<T>
>;
type ParseQuery<T extends ParseObject = ParseObject> = InstanceType<
  typeof Parse.Query<T>
>;

function asCase(object: ParseObject<Attributes>): Case {
  const c: Case = { id: object.id ?? randomId(), name: object.get("title") };
  c.claimKind = object.get("claimKind");
  c.timeline = object.get("timeline");
  c.bookedReserves = object.get("bookedReserves");
  c.settlementChances = object.get("settlementChances");
  c.settlementAdjusted = object.get("settlementAdjusted");
  c.judgment = object.get("judgment");
  c.closing = object.get("closing");
  c.parties = object.get("parties") || [];
  c.claims = object.get("claims") || [];
  c.damages = object.get("damages") || [];
  c.budgets = object.get("budgets") || [];
  c.costStructure = object.get("costStructure") || {};
  c.settlements = object.get("settlements") || [];
  c.agreements = object.get("agreements") || [];
  c.results = object.get("results");
  // precomputed in beforeSave and already on the wire (queryCase excludes only
  // `variantResults`); carries the settlement records/realities the case views
  // read rather than re-deriving. Absent on cases not yet re-saved.
  c.portfolioSummary = object.get("portfolioSummary");
  c.variants = object.get("variants") || {};
  c.variantResults = object.get("variantResults") || {};
  c.createdAt = object.createdAt?.toISOString();
  c.updatedAt = object.updatedAt?.toISOString();
  // stamped in beforeSave from the authenticated session. Absent on cases saved
  // before attribution existed, and on those whose creator could not be
  // recovered from the ACL by the backfill.
  c.createdBy = object.get("createdBy");
  c.updatedBy = object.get("updatedBy");
  c.accessList = object.getACL()?.toJSON();
  // a case stored in an older shape is read as it is saved (see withDamages,
  // withSettlementCostTypes and withCostItems)
  return withCostItems(withSettlementCostTypes(withDamages(c)));
}

/**
 * The names an access list can know a user under: their id, and the role name
 * of every role they hold, be it as a member or through a role that is itself
 * a member of another.
 *
 * Asked with the user's own session, so a role counts only if its row is
 * readable by its members (see README.md, "Roles"). A role that is not is
 * missing here, and what it grants with it: the server still lets its members
 * write, the app shows them the case as read-only.
 */
async function accessNamesOf(
  user: ParseObject,
  options: { sessionToken: string },
): Promise<string[]> {
  if (user.id === undefined) {
    console.warn("Cannot load roles of user with undefined id");
    return [];
  }

  const visitedRoleIds = new Set<string>();
  const names = new Set([user.id]);
  try {
    let roles = await new Parse.Query(Parse.Role)
      .equalTo("users", user)
      .find(options);
    // `roles` holds unvisited roles only: first those the user is a member of,
    // then round by round those the roles of the round before are members of
    while (roles.length > 0) {
      for (const role of roles) {
        // a stored role has an id and a name: Parse Server requires both
        if (role.id !== undefined) visitedRoleIds.add(role.id);
        const name = role.getName();
        if (name !== null) names.add(roleName(name));
      }
      const parents = await new Parse.Query(Parse.Role)
        .containedIn("roles", roles)
        .find(options);
      roles = parents.filter(({ id }) => id && !visitedRoleIds.has(id));
    }
  } catch (error) {
    // as for a role that cannot be read: its grants are not known
    console.warn("Could not load the roles of the user", error);
  }
  return Array.from(names);
}

export class ParseCaseClient implements CaseApi {
  private readonly options;

  constructor(sessionToken: string) {
    this.options = {
      sessionToken,
    };
  }

  getCasesList(options?: QueryOptions): Promise<CaseListElement[]> {
    return Parse.Cloud.run("getCasesList", options ?? {}).then(
      (casesList: CaseListElement[]) => casesList,
    );
  }
  getCaseCounts(filter?: CaseFilter): Promise<CaseCounts> {
    return Parse.Cloud.run("getCaseCounts", { filter });
  }

  queryAccessNames(): Promise<string[]> {
    const user = Parse.User.current();
    // without a user nothing but public access applies
    return user ? accessNamesOf(user, this.options) : Promise.resolve([]);
  }

  getPortfolioStats(filter?: CaseFilter): Promise<PortfolioStats> {
    return Parse.Cloud.run("getPortfolioStats", { filter });
  }

  getSettlementPortfolio(filter?: CaseFilter): Promise<SettlementPortfolio> {
    return Parse.Cloud.run("getSettlementPortfolio", { filter });
  }

  getPortfolioOverview(): Promise<PortfolioOverview> {
    return Parse.Cloud.run("getPortfolioOverview");
  }

  private saveCase(c: ParseObject<Attributes>): Promise<void> {
    return c.save().then();
  }

  queryCase(caseId: string): Promise<Case> {
    const caseQuery = new Parse.Query("Case");
    caseQuery.exclude("variantResults");
    return caseQuery.get(caseId, this.options).then((obj) => asCase(obj));
  }

  queryVariantResults(
    caseId: string,
  ): Promise<{ [variantId: string]: Results }> {
    const caseQuery = new Parse.Query("Case");
    caseQuery.select("variantResults");
    return caseQuery
      .get(caseId, this.options)
      .then((obj) => obj.get("variantResults") || {});
  }

  generateDecisionTrees(
    caseId: string,
    onProgress: (share: number) => void,
    signal: AbortSignal,
  ): Promise<DecisionTreeResult> {
    return fetchDecisionTrees(
      (name, params) => Parse.Cloud.run(name, params),
      caseId,
      onProgress,
      signal,
    );
  }

  addCase(c: Case): Promise<Case> {
    const CaseType = Parse.Object.extend("Case");
    const parseCase = new CaseType();
    parseCase.set("title", c.name);

    // current users and owners should have access
    const acl = new ACL(Parse.User.current());
    acl.setRoleReadAccess("owners", true);
    acl.setRoleWriteAccess("owners", true);
    parseCase.setACL(acl);

    return parseCase
      .save(null, this.options)
      .then(({ id }: typeof CaseType) => ({
        id,
        title: parseCase.get("name"),
      }));
  }

  importCase(
    config: CaseExport,
    { overwriteId }: { overwriteId?: string },
  ): Promise<Case> {
    const applyConfig = (parseCase: ParseObject<Attributes>) => {
      const params = toCaseParameters(config);
      // who booked them elsewhere stays, as imported (see stampBookings)
      params.bookedReserves = importedBookings(params.bookedReserves);
      parseCase.set("title", params.name); // `name` lives in the `title` column
      for (const key of CASE_PARAMETERS_KEYS) {
        // id is the Parse objectId (not a column); name is handled above. Every
        // other parameter key maps 1:1 to a same-named Parse column.
        if (key === "id" || key === "name") continue;
        parseCase.set(key, params[key]);
      }
    };

    if (overwriteId) {
      // update the existing case's configuration in place, keeping its ACL.
      // beforeSave recomputes results/variants/budgets.
      return this.getCase(overwriteId).then((parseCase) => {
        applyConfig(parseCase);
        return parseCase
          .save(null, this.options)
          .then((saved) => asCase(saved));
      });
    }

    // create a fresh case (new id), mirroring addCase's ACL setup
    const CaseType = Parse.Object.extend("Case");
    const parseCase = new CaseType();
    applyConfig(parseCase);

    const acl = new ACL(Parse.User.current());
    acl.setRoleReadAccess("owners", true);
    acl.setRoleWriteAccess("owners", true);
    parseCase.setACL(acl);

    // a new case takes the reserve history of the file; one overwritten
    // keeps its own
    return parseCase
      .save(null, {
        ...this.options,
        context: { reserveHistory: config.reserveHistory },
      })
      .then((saved: ParseObject<Attributes>) => asCase(saved));
  }

  removeCase(caseId: string): Promise<void> {
    const CaseType = Parse.Object.extend("Case");
    const c = CaseType.createWithoutData(caseId);
    return c.destroy().then();
  }

  setTitle(
    caseId: string,
    title: string,
    claimKind?: ClaimKind,
  ): Promise<void> {
    const CaseType = Parse.Object.extend("Case");
    const c = CaseType.createWithoutData(caseId);
    c.set("title", title);
    if (claimKind) c.set("claimKind", claimKind);
    return c.save().then();
  }

  add<E>(caseId: string, fieldId: keyof Case, element: E): Promise<E[]> {
    return this.getCase(caseId).then((c) => {
      const elements: E[] = c.get(fieldId) || [];
      const newElements = [...elements, element];
      c.set(fieldId, newElements);
      return c.save().then(() => newElements);
    });
  }

  // A case stored in an older shape is changed as the app read it: converted,
  // the way asCase converts it (see withDamages, withSettlementCostTypes and
  // withCostItems).
  private getCase(caseId: string): Promise<ParseObject<Attributes>> {
    const caseQuery = new Parse.Query("Case");
    return caseQuery.get(caseId, this.options).then((object) => {
      const stored = {
        claims: object.get("claims") || [],
        damages: object.get("damages") || [],
        settlements: object.get("settlements") || [],
        parties: object.get("parties") || [],
        budgets: object.get("budgets") || [],
      };
      const read = withCostItems(withSettlementCostTypes(withDamages(stored)));
      if (read !== stored) {
        object.set("claims", read.claims);
        object.set("damages", read.damages);
        object.set("settlements", read.settlements);
        object.set("budgets", read.budgets);
      }
      return object;
    });
  }

  update(
    caseId: string,
    fieldId: keyof Case,
    element: { id: string },
  ): Promise<void> {
    return this.getCase(caseId).then((c) => {
      const elements: { id: string }[] = c.get(fieldId) || [];
      if (elements === undefined || elements === null) {
        // nothing to remove
        return;
      }
      c.set(
        fieldId,
        elements.map((p) => (p.id === element.id ? element : p)),
      );
      return this.saveCase(c);
    });
  }

  remove(
    caseId: string,
    fieldId: keyof Case,
    elementId: string,
  ): Promise<void> {
    return this.getCase(caseId).then((c) => {
      const elements: { id: string }[] = c.get(fieldId);
      if (elements === undefined || elements === null) {
        // nothing to remove
        return;
      }
      const newElements = elements.filter(
        (element) => element.id !== elementId,
      );
      if (newElements.length === elements.length) {
        // nothing changed
        return;
      }
      c.set(fieldId, newElements);
      return this.saveCase(c);
    });
  }

  addParty(caseId: string, party: Party): Promise<Party[]> {
    return this.getCase(caseId).then((c) => {
      const parties: Party[] = c.get("parties") || [];
      const newParties = [...parties, party];
      c.set("parties", newParties);
      return this.saveCase(c).then(() => newParties);
    });
  }

  updateParty(caseId: string, prototype: Party): Promise<void> {
    return this.update(caseId, "parties", prototype);
  }

  updateApportionment(
    caseId: string,
    party: Party,
    budget: Budget,
  ): Promise<void> {
    return this.getCase(caseId).then((c) => {
      const parties: Party[] = c.get("parties") || [];
      c.set(
        "parties",
        parties.map((other) => (other.id === party.id ? party : other)),
      );
      const budgets: Budget[] = c.get("budgets") || [];
      c.set(
        "budgets",
        budgets.map((other) =>
          other.partyId === budget.partyId ? budget : other,
        ),
      );
      return this.saveCase(c);
    });
  }

  removeParty(caseId: string, partyId: string): Promise<void> {
    return this.remove(caseId, "parties", partyId);
  }

  addClaim(caseId: string, claim: Claim): Promise<Claim[]> {
    return this.add(caseId, "claims", claim);
  }

  updateClaim(caseId: string, claim: Claim, damage?: Damage): Promise<void> {
    if (damage === undefined) return this.update(caseId, "claims", claim);
    // the claim and the damage, new or renamed, in one save
    return this.getCase(caseId).then((c) => {
      const claims: Claim[] = c.get("claims") || [];
      c.set(
        "claims",
        claims.map((other) => (other.id === claim.id ? claim : other)),
      );
      const damages: Damage[] = c.get("damages") || [];
      c.set("damages", [
        ...damages.filter(({ id }) => id !== damage.id),
        damage,
      ]);
      return this.saveCase(c);
    });
  }

  removeClaim(caseId: string, claimId: string): Promise<void> {
    return this.getCase(caseId).then((c) => {
      const claims: { id: string }[] = c.get("claims") || [];
      const newClaims = claims.filter((claim) => claim.id !== claimId);
      c.set("claims", newClaims);

      // Clean up any settlement offers that reference the deleted claim
      const settlements: SettlementOffer[] = c.get("settlements") || [];
      const newSettlements = settlements.map((offer) => ({
        ...offer,
        settledClaims: (offer.settledClaims ?? []).filter(
          (id) => id !== claimId,
        ),
      }));
      c.set("settlements", newSettlements);

      return this.saveCase(c);
    });
  }

  addOffer(caseId: string, offer: SettlementOffer): Promise<SettlementOffer[]> {
    return this.add(caseId, "settlements", offer);
  }

  updateOffer(caseId: string, offer: SettlementOffer): Promise<void> {
    return this.update(caseId, "settlements", offer);
  }

  removeOffer(caseId: string, offerId: string): Promise<void> {
    return this.remove(caseId, "settlements", offerId);
  }

  addBudget(caseId: string, budget: Budget): Promise<Budget[]> {
    return this.add(caseId, "budgets", budget);
  }

  updateBudget(caseId: string, budget: Budget): Promise<void> {
    return this.getCase(caseId).then((c) => {
      const budgets: Budget[] = c.get("budgets") || [];
      if (!budgets) {
        // nothing to remove
        return;
      }
      const newBudgets = budgets.map((b) =>
        b.partyId === budget.partyId ? budget : b,
      );
      c.set("budgets", newBudgets);
      return this.saveCase(c);
    });
  }

  removeBudget(caseId: string, partyId: string): Promise<void> {
    return this.getCase(caseId).then((c) => {
      const budgets: Budget[] = c.get("budgets");
      if (!budgets) {
        // nothing to remove
        return;
      }
      const newBudgets = budgets.filter((b) => b.partyId !== partyId);
      if (newBudgets.length === budgets.length) {
        // nothing changed
        return;
      }
      c.set("budgets", newBudgets);
      return this.saveCase(c);
    });
  }

  setPartyAgreements(
    caseId: string,
    partyAgreements: PartyAgreements,
  ): Promise<void> {
    return this.getCase(caseId).then((c) => {
      const agreements: PartyAgreements[] = c.get("agreements") ?? [];
      const newAgreements = agreements.filter(
        ({ partyId }) => partyId !== partyAgreements.partyId,
      );
      newAgreements.push(partyAgreements);
      c.set("agreements", newAgreements);
      return this.saveCase(c);
    });
  }

  setBargainingVariant(caseId: string, variant: Configuration): Promise<void> {
    return this.getCase(caseId).then((c) => {
      const variants: Variants = c.get("variants") ?? {};
      variants.bargaining = variant;
      console.log(`Saving new bargaining variant`);
      c.set("variants", variants);
      return this.saveCase(c);
    });
  }

  closeStage(
    caseId: string,
    timeline: Timeline,
    budgets: Budget[],
    record: Pick<Case, "judgment" | "closing"> = {},
  ): Promise<void> {
    return this.getCase(caseId).then((c) => {
      const changed = new Map(
        budgets.map((budget) => [budget.partyId, budget]),
      );
      const all: Budget[] = c.get("budgets") || [];
      c.set("timeline", timeline);
      if (record.judgment) c.set("judgment", record.judgment);
      if (record.closing) c.set("closing", record.closing);
      c.set(
        "budgets",
        all.map((budget) => changed.get(budget.partyId) ?? budget),
      );
      return this.saveCase(c);
    });
  }

  updateBookedReserves(
    caseId: string,
    bookedReserves: BookedReserve[],
  ): Promise<void> {
    const CaseType = Parse.Object.extend("Case");
    const c = CaseType.createWithoutData(caseId);
    c.set("bookedReserves", bookedReserves);
    return this.saveCase(c);
  }

  updateSettlementChances(
    caseId: string,
    settlementChances: SettlementChances[],
  ): Promise<void> {
    const CaseType = Parse.Object.extend("Case");
    const c = CaseType.createWithoutData(caseId);
    c.set("settlementChances", settlementChances);
    return this.saveCase(c);
  }

  updateTimeline(caseId: string, timeline: Timeline): Promise<void> {
    const CaseType = Parse.Object.extend("Case");
    const c = CaseType.createWithoutData(caseId);
    c.set("timeline", timeline);
    return this.saveCase(c);
  }

  updateCostStructure(caseId: string, structure: CostStructure): Promise<void> {
    const CaseType = Parse.Object.extend("Case");
    const c = CaseType.createWithoutData(caseId);
    c.set("costStructure", structure);
    console.debug("Updating cost structure");
    return this.saveCase(c).then(() => console.debug("Updated cost structure"));
  }
}

export function useCaseApi() {
  return useContext(CaseApiContext);
}

export const ParseCaseApiProvider = ({
  children,
}: {
  children: ReactNode | ReactNode[];
}) => {
  const sessionToken = getParseSessionToken();
  const parseCaseApi: CaseApi = new ParseCaseClient(sessionToken);
  // make sure budgets are initialized and cleaned-up correctly
  const api: CaseApi = new BudgetInitializingCaseApiDecorator(parseCaseApi);
  return (
    <CaseApiContext.Provider value={api}>{children}</CaseApiContext.Provider>
  );
};

function getParseSessionToken() {
  const sessionToken = Parse.User.current()?.getSessionToken();
  if (sessionToken) {
    // parse client already logged in
    return sessionToken;
  }

  const storedToken = parseAuth.getToken();
  if (storedToken !== undefined) {
    return storedToken;
  }

  console.log("Missing session token for parse server");
  throw new Error("Missing session token for server. User sign-in is needed.");
}
